<script>
import { firestore as db } from "@/plugins/firebase.app";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import KalustoEdit from "@/views/KalustoEdit.vue";
import PictureDialog from "@/views/PictureDialog.vue";
import QrCodeScannerDialog from "@/views/QrCodeScannerDialog.vue";
import { jsPDF } from "jspdf";
import QRCodeStyling from "qr-code-styling";
import { debounce, orderBy } from "lodash";
import ModalPicker from "@/components/ModalPicker.vue";
import dayjs from "dayjs";
import KalustoSijainnitDialog from "@/components/KalustoSijainnitDialog.vue";
import KalustoSijaintiEditDialog from "@/components/KalustoSijaintiEditDialog.vue";

const locationsTable = "kalusto_sijainnit";
const equipmentTable = "kalusto";
const equipmentTypesTable = "kalusto_tyypit";
export default {
  name: "KalustoView",
  components: {
    KalustoSijaintiEditDialog,
    KalustoSijainnitDialog,
    ModalPicker,
    QrCodeScannerDialog,
    PictureDialog,
    KalustoEdit,
  },
  data() {
    return {
      locations: [], // Raw flat locations from Firestore
      openNodes: [], // Tracks which nodes are expanded
      selectedNodes: [], // Tracks which nodes are selected
      equipmentTypes: [],
      selectedMoveNodesLocation: [],

      loadingEquipments: false,
      loadingMoveEquipment: false,
      loadingPrint: false,

      equipments: [],
      search: "", // Search term
      searchDebounced: "",

      selectableTree: false,
      // Equipment dialog
      dialogEquipment: false,
      dialogMoveEquipment: false,
      dialogMoveEquipmentSelectedLocation: null,
      selectedPicture: null,
      selectedThumbnail: null,
      selectedPictureDialog: false,
      loadingEquipmentSave: false,
      showQrCodeScanner: false,
      detectedCodes: null,
      dialogPrintLabels: false,
      selectedPrintType: "all",
      selectedPrintDate: dayjs().startOf("day").format("YYYY-MM-DD"),
      selectedPrintIds: "",
      dialogMoveTo: false,
      moveToEquipmentId: "",
    };
  },
  watch: {
    dialogEquipment(value) {
      if (!value) {
        this.dialogEquipmentData = {};
      }
    },
    selectedPicture() {
      if (this.selectedPicture) {
        this.selectedPictureDialog = true;
      } else {
        this.selectedPictureDialog = false;
      }
    },
    search(val) {
      this.debouncedUpdateSearch(val);
    },
    searchDebounced(val) {
      if (val) {
        this.openNodes = this.locations.map((node) => node.id);
      }
      if (val === "") {
        this.openNodes = [];
      }
    },
    dialogMoveEquipment(value) {
      if (!value) {
        this.dialogMoveEquipmentSelectedLocation = null;
      }
    },
  },
  computed: {
    treeData() {
      // Step 1: Create a map of all locations with an initial structure
      const map = new Map(
        this.locations.map((loc) => [
          loc.id,
          { ...loc, children: [], _fullTree: "" },
        ])
      );

      // Step 2: Assign children to their parents
      map.forEach((node) => {
        if (node.parent) {
          const parent = map.get(node.parent);
          if (parent) {
            parent.children.push(node);
          }
        }
      });

      // Step 3: Build the tree with _fullTree calculated dynamically
      const tree = [];
      const calculateFullTree = (node, parentFullTree = "") => {
        const fullTree = parentFullTree
          ? `${parentFullTree} -> ${node.name}`
          : node.name;
        return {
          ...node,
          _name: node.name,
          _fullTree: fullTree,
          icon: "mdi mdi-map-marker-outline",
          children: [
            ...orderBy(
              node.children.map((child) => calculateFullTree(child, fullTree)),
              ["_name"],
              ["asc"]
            ),
            ...orderBy(
              this.equipments
                .filter((e) => e.locationId === node.id)
                .map((e) => ({
                  ...e,
                  _name: [e.name, e.id].join(" - "),
                  _fullTree: `${fullTree} -> ${e.name}`,
                  _isEquipment: true,
                })),
              ["_name"],
              ["asc"]
            ),
          ],
        };
      };

      map.forEach((node) => {
        if (!node.parent) {
          tree.push(calculateFullTree(node)); // Root nodes start the recursion
        }
      });

      return tree;
    },
    /*equipmentTypeTree() {
      const map = new Map(
        this.equipmentTypes.map((type) => [
          type.id,
          { ...type, children: [], _fullTree: "" },
        ])
      );
      const tree = [];

      map.forEach((node) => {
        if (node.parent) {
          const parent = map.get(node.parent);
          node._fullTree = parent._fullTree
            ? [parent._fullTree, node.name].join(" -> ")
            : node.name;
          //map.get(node.parent)?.children?.push(node);
          tree.push(node);
        } else {
          node._fullTree = node.name;
          tree.push(node);
        }
      });
      return tree;
    },*/
    selectedEquipments() {
      if (this.selectedNodes.length === 0) {
        return [];
      }
      // Käytetään flattenedEquipmentTreeData, koska se pitää sisällään _fullTree tiedon
      return this.flattenedEquipmentTreeData.filter((equipment) =>
        this.selectedNodes.includes(equipment.id)
      );
    },
    locationTreeData() {
      // only list treedata where _isEquipment is not true
      const filterNonEquipment = (node) => {
        if (node._isEquipment) {
          return null;
        }
        return {
          ...node,
          children: node.children
            ?.filter((n) => !n._isEquipment)
            .map(filterNonEquipment)
            .filter(Boolean),
        };
      };
      return this.treeData.map(filterNonEquipment).filter(Boolean);
    },
    flattenedEquipmentTreeData() {
      const flatten = (node) => {
        return [
          { ...node, children: undefined },
          ...(node.children?.flatMap(flatten) || []),
        ];
      };
      return this.treeData
        .flatMap(flatten)
        .filter(Boolean)
        .filter((e) => e._isEquipment);
    },
  },
  methods: {
    // Fetch all locations from Firestore
    async fetchLocations() {
      const snapshot = await getDocs(
        query(collection(db, locationsTable), where("deleted", "==", false))
      );
      this.locations = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    },
    async fetchEquipments() {
      this.loadingEquipments = true;
      try {
        const snapshot = await getDocs(
          query(collection(db, equipmentTable), where("deleted", "==", false))
        );
        this.equipments = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          icon: "mdi-toy-brick",
        }));
      } catch (e) {
        console.error(e);
        this.$toast("Kaluston haku epäonnistui", { type: "error" });
      }
      this.loadingEquipments = false;
    },
    // Fetch equipment types for autocomplete
    async fetchEquipmentTypes() {
      const snapshot = await getDocs(collection(db, equipmentTypesTable));
      this.equipmentTypes = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    },

    // Build hierarchical tree data

    // Add a new node
    addNode(parent) {
      this.$refs.kalustoSijaintiEditDialog.open({
        name: "",
        parent: parent?.id || null,
        _fullTree: parent?._fullTree || null,
        deleted: false,
      });
    },

    // Edit an existing node
    editNode(item) {
      this.$refs.kalustoSijaintiEditDialog.open({ ...item });
    },

    // Delete a node
    async deleteNode(item) {
      if (
        await this.$deleteConfirm(
          `Haluatko varmasti poistaa sijainnin ${item.name}?`
        )
      ) {
        if (item.children && item.children.length > 0) {
          this.$toast(
            "Sijaintia ei voi poistaa, koska sillä on alisijainteja",
            {
              type: "error",
            }
          );
          return;
        }
        await deleteDoc(doc(db, locationsTable, item.id));
        this.fetchLocations(); // Refresh data
      }
    },
    // Edit an existing node
    editEquipment(item) {
      /*if (item.typeId) {
        const type = this.equipmentTypeTree.find((t) => t.id === item.typeId);
        this.dialogEquipmentData.typeFullTree = type._fullTree;
      }
      this.dialogEquipment = true;*/
      this.dialogEquipment = true;
      this.$nextTick(() => {
        this.$refs?.kalustoEdit?.editEquipment(item.id);
      });
    },

    viewEquipment(item) {
      /*if (item.typeId) {
        const type = this.equipmentTypeTree.find((t) => t.id === item.typeId);
        this.dialogEquipmentData.typeFullTree = type._fullTree;
      }
      this.dialogEquipment = true;*/
      this.dialogEquipment = true;
      this.$nextTick(() => {
        this.$refs?.kalustoEdit?.viewEquipment(item.id);
      });
    },
    // Add equipment to the selected location
    addEquipment(item) {
      this.dialogEquipment = true;
      this.$nextTick(() => {
        this.$refs?.kalustoEdit?.addEquipment(item);
      });
    },
    openQrCodeScanner() {
      this.showQrCodeScanner = true;
    },
    async onDetect(code) {
      try {
        const snapshot = await getDoc(doc(db, equipmentTable, code));
        if (snapshot.exists()) {
          this.showQrCodeScanner = false;
          await this.$router.push({
            name: "KalustoById",
            params: { id: code },
            query: { fromQr: true },
          });
        } else {
          this.$toast("Kalustoa ei löytynyt annetulla ID-koodilla " + code, {
            type: "error",
          });
        }
      } catch (e) {
        console.error(e);
        this.$toast("Virhe haettaessa kalustoa", { type: "error" });
      }
    },
    confirmLocationSelectionForMove(location) {
      if (location) {
        this.dialogMoveEquipmentSelectedLocation = location;
      } else {
        this.dialogMoveEquipmentSelectedLocation = null;
      }
    },
    async moveSelectedEquipments() {
      if (
        await this.$confirm(
          `Haluatko varmasti siirtää valitun kaluston sijaintiin ${
            this.dialogMoveEquipmentSelectedLocation?._fullTree ||
            this.dialogMoveEquipmentSelectedLocation?.name
          }?`,
          {
            title: "Vahvista siirto",
            buttonTrueText: "Siirrä",
            buttonFalseText: "Peruuta",
          }
        )
      ) {
        this.loadingMoveEquipment = true;
        const batch = writeBatch(db);

        this.selectedEquipments.forEach((equipment) => {
          const equipmentRef = doc(db, equipmentTable, equipment.id);
          batch.update(equipmentRef, {
            locationId: this.dialogMoveEquipmentSelectedLocation.id,
            location: this.dialogMoveEquipmentSelectedLocation?._fullTree,
          });
        });

        try {
          await batch.commit();
          this.selectedNodes = [];
          this.dialogMoveEquipment = false;
        } catch (error) {
          console.error(error);
          this.$toast("Virhe siirrettäessä kalustoa", { type: "error" });
        }

        await this.fetchEquipments();
        this.loadingMoveEquipment = false;
      }
    },
    async moveToEquipment(code) {
      if (code?.length) {
        await this.$router.push({
          name: "KalustoById",
          params: { id: code },
          query: { fromQr: true },
        });
      }
    },
    printLabelsWithSelectedEquipments() {
      if (this.selectedPrintType === "all") {
        this.printLabels();
      } else if (this.selectedPrintType === "id") {
        const ids = this.selectedPrintIds
          .split("\n")
          .filter(Boolean)
          .map((i) => i.trim().toLowerCase());
        this.printLabels(
          this.equipments.filter((e) => ids.includes(e.id?.toLowerCase()))
        );
      } else if (this.selectedPrintType === "date") {
        this.printLabels(
          this.equipments.filter((e) =>
            dayjs(e.createdAt).isSameOrAfter(this.selectedPrintDate, "day")
          )
        );
      }
      this.dialogPrintLabels = false;
    },
    async printLabels(equipments) {
      if (!equipments || equipments.length === 0) {
        equipments = this.equipments;
      }
      this.loadingPrint = true;
      try {
        const equipmentTypeTree = () => {
          // Create map of types
          const map = new Map(
            this.equipmentTypes.map((type) => [
              type.id,
              { ...type, children: [], _fullTree: "" },
            ])
          );

          // Link children to their parents (if needed)
          for (const [id, node] of map.entries()) {
            if (node.parent && map.has(node.parent)) {
              map.get(node.parent).children.push(node);
            }
          }

          // Recursive function to build _fullTree
          function buildFullTree(id, map) {
            const node = map.get(id);
            if (!node) return "";

            // If _fullTree already computed, return it
            if (node._fullTree) {
              return node._fullTree;
            }

            // If no parent, _fullTree is just the node's name
            if (!node.parent) {
              node._fullTree = " "; //node.name;
              return node._fullTree;
            }

            // Otherwise, recursively get parent's _fullTree and append current node
            const parentFullTree = buildFullTree(node.parent, map);
            node._fullTree = `${
              parentFullTree?.trim() ? parentFullTree.trim() + " /" : ""
            } ${node.name}`;
            return node._fullTree;
          }

          // Populate _fullTree for all
          for (const [id] of map.entries()) {
            buildFullTree(id, map);
          }

          const tree = [];
          for (const [, node] of map.entries()) {
            tree.push(node);
          }
          return tree;
        };
        const ett = equipmentTypeTree();

        const doc = new jsPDF({
          orientation: "landscape",
          unit: "cm",
          format: [2.5, 6],
        });

        let i = 0;
        for (let equipment of equipments) {
          //doc.text(equipment.id, 1, 1);
          const type = ett.find((t) => t.id === equipment.typeId);
          if (type) {
            equipment.typeFullTree = type._fullTree;
          }
          const options = {
            width: 150,
            height: 150,
            type: "svg",
            data: equipment.id,
            //image: "/img/icons/apple-touch-icon-76x76.png",
            margin: 5,
            qrOptions: {
              typeNumber: 0,
              mode: "Byte",
              errorCorrectionLevel: "Q",
            },
            imageOptions: {
              hideBackgroundDots: true,
              margin: 0,
              crossOrigin: "anonymous",
            },
            dotsOptions: {
              color: "#000000",
              type: "rounded",
            },
            backgroundOptions: {
              color: "#ffffff",
            },
            cornersSquareOptions: {
              color: "#000000",
              //type: "rounded",
            },
            cornersDotOptions: {
              color: "#000000",
              //type: "dot",
            },
          };
          let qrCode = new QRCodeStyling(options);
          let image = await qrCode.getRawData("png");

          const arrayBuffer = await image.arrayBuffer(); // Convert Blob to ArrayBuffer

          doc.setFontSize(10);
          doc.text(equipment.id?.trim(), 0.25, 2.3);
          doc.setFontSize(6);
          doc.text("Noormarkku AS51", 4, 2.3);
          doc.setFontSize(7);
          doc.setLineWidth(1);
          let wrappedText = doc.splitTextToSize(
            `${equipment.typeFullTree} / ${equipment.name}`,
            2
          );

          //['Ajoneuvojen ja', 'veneiden', 'varusteet ja', 'tarvikkeet / Pelast', 'autumistarvikkeet', '/ Pelastusrenkaat', 'ja -laudat / Resca ', 'Pintapelastuslaut', 'a']
          if (wrappedText.length >= 9) {
            doc.setFontSize(6.5);
            wrappedText = doc.splitTextToSize(
              `${equipment.typeFullTree} / ${equipment.name}`,
              2
            );
          }

          doc.text(wrappedText, 1.9, 0.4);
          doc.addImage({
            imageData: new Uint8Array(arrayBuffer),
            x: 3.9,
            y: 0.1,
            width: 1.9,
            height: 1.9,
          });
          doc.addImage({
            imageData:
              "data:image/jpeg;base64,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",
            x: 0.1,
            y: 0.2,
            width: 1.8,
            height: 1.8,
          });

          if (i < equipments.length - 1) {
            doc.addPage();
          }
          i++;
        }
        if (equipments.length === 1) {
          doc.save(`kalusto_labels_${equipments?.[0]?.id}.pdf`);
        } else {
          doc.save("kalusto_labels.pdf");
        }
      } catch (e) {
        console.error(e);
        this.$toast("Virhe tulostuksessa", { type: "error" });
      }
      this.loadingPrint = false;
    },
  },
  created() {
    this.debouncedUpdateSearch = debounce((value) => {
      this.searchDebounced = value;
    }, 500);
  },
  mounted() {
    this.fetchLocations();
    this.fetchEquipmentTypes();
    this.fetchEquipments();
  },
};
</script>

<template>
  <v-row>
    <v-container fluid>
      <v-card class="fill-height" flat>
        <v-card-title>
          <v-icon class="mr-2">mdi-bus-marker</v-icon>
          Kalusto
          <v-spacer />

          <v-btn
            icon
            @click="dialogPrintLabels = true"
            class="mr-5"
            title="Tulosta kalustotarrat"
            :loading="loadingPrint"
            :disabled="equipments?.length === 0"
          >
            <v-icon>mdi mdi-label-multiple-outline</v-icon>
          </v-btn>
          <v-btn
            icon
            @click="openQrCodeScanner"
            title="Avaa QR-koodiskanneri"
            class="mr-5"
          >
            <v-icon>mdi mdi-qrcode-scan</v-icon>
          </v-btn>
          <v-btn
            icon
            @click="dialogMoveTo = true"
            title="Mene suoraan kalustoon"
          >
            <v-icon>mdi mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Hae"
                single-line
                hide-details
                clearable
              >
              </v-text-field>
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-5">
            <v-btn
              @click="openNodes = locations.map((node) => node.id)"
              :disabled="openNodes.length === locations.length"
              text
              small
              class="mr-2"
            >
              <v-icon>mdi mdi-unfold-more-horizontal</v-icon>
              Avaa
            </v-btn>

            <v-btn
              @click="openNodes = []"
              :disabled="openNodes.length === 0"
              text
              small
            >
              <v-icon>mdi mdi-unfold-less-horizontal</v-icon>
              Sulje
            </v-btn>
            <v-spacer />
            <v-btn
              @click="selectableTree = !selectableTree"
              text
              small
              class="mr-2"
            >
              <v-icon small>mdi mdi-checkbox-multiple-blank-outline</v-icon
              >&nbsp;
              {{
                !selectableTree
                  ? "Aktivoi monivalinta"
                  : "Deaktivoi monivalinta"
              }}
            </v-btn>
            <v-btn @click="addNode()" text small>
              <v-icon>mdi mdi-plus</v-icon> Päätaso
            </v-btn>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-text v-if="selectableTree">
          <v-row
            ><v-col cols="auto">
              <span class="text-subtitle-1">Valinnat: </span>
              <span class="text-subtitle-1 font-weight-bold">{{
                selectedNodes.length
              }}</span>
              <span
                ><v-btn
                  icon
                  small
                  class="ml-2"
                  title="Poista valinnat"
                  @click="selectedNodes = []"
                  ><v-icon>mdi mdi-delete-empty-outline</v-icon></v-btn
                ></span
              >
            </v-col>
            <v-col cols="auto">
              <v-btn
                @click="printLabels(selectedEquipments)"
                :disabled="selectedEquipments.length === 0"
                text
                small
              >
                <v-icon small>mdi mdi-label-outline</v-icon>&nbsp;Tulosta
                kalustotarrat
              </v-btn>
            </v-col>
            <v-col cols="auto">
              <v-btn
                @click="dialogMoveEquipment = true"
                :disabled="selectedEquipments.length === 0"
                text
                small
              >
                <v-icon small>mdi mdi-move-resize-variant</v-icon>&nbsp;Siirrä
                kalusto eri sijaintiin
              </v-btn>
            </v-col>
          </v-row></v-card-text
        >
        <v-divider v-if="selectableTree" />
        <v-card-text :class="[$vuetify.breakpoint.xsOnly && 'pa-0']">
          <v-progress-linear
            indeterminate
            v-if="loadingEquipments"
          ></v-progress-linear>
          <v-treeview
            v-model="selectedNodes"
            :items="treeData"
            :activatable="false"
            item-value="id"
            item-text="_name"
            open-on-click
            :open.sync="openNodes"
            :search="searchDebounced"
            :selectable="selectableTree"
            selection-type="independent"
          >
            <template v-slot:label="{ item }">
              <v-tooltip nudge-left="70" left>
                <template v-slot:activator="{ on }">
                  <div v-on="on">
                    <div>
                      <v-row no-gutters align-content="center">
                        <v-row no-gutters align-content="center">
                          <div style="text-wrap: auto">
                            {{ item.name }}
                            <div
                              v-if="item._isEquipment"
                              class="font-italic caption"
                            >
                              {{ item.id }}
                            </div>
                          </div>
                        </v-row>
                        <v-img
                          v-if="item.pictureThumbnail"
                          :src="item.pictureThumbnail"
                          max-height="50"
                          max-width="50"
                          width="50"
                          contain
                          @click="
                            () => {
                              if (item._isEquipment) {
                                viewEquipment(item);
                              } else {
                                editNode(item);
                              }
                            }
                          "
                        />
                      </v-row>
                    </div>
                  </div>
                </template>
                <div>{{ item.name }}</div>
                <div v-if="item._isEquipment" class="font-italic caption">
                  {{ item.id }}
                </div>
              </v-tooltip>
            </template>
            <template v-slot:prepend="{ item }">
              <v-icon
                small
                @click="
                  () => {
                    if (item._isEquipment) {
                      viewEquipment(item);
                    } else {
                      editNode(item);
                    }
                  }
                "
                >{{ item.icon }}
              </v-icon>
            </template>
            <template v-slot:append="{ item }">
              <v-menu :close-on-content-click="true">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" icon>
                    <v-icon>mdi-dots-vertical</v-icon>
                    <!-- A menu icon to open the menu -->
                  </v-btn>
                </template>

                <!-- Menu content -->
                <v-list dense>
                  <v-list-item v-if="!item._isEquipment" @click="addNode(item)">
                    <v-list-item-icon>
                      <v-icon class="mr-2">mdi-plus</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content
                      >Lisää sijainti alle</v-list-item-content
                    >
                  </v-list-item>

                  <v-list-item
                    v-if="item._isEquipment"
                    @click="viewEquipment(item)"
                  >
                    <v-list-item-icon>
                      <v-icon class="mr-2">mdi-arrow-right</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>Avaa kalusto</v-list-item-content>
                  </v-list-item>
                  <!--<v-list-item
                    v-if="item._isEquipment"
                    @click="editEquipment(item)"
                  >
                    <v-list-item-icon>
                      <v-icon class="mr-2">mdi-pencil</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>Muokkaa kalustoa</v-list-item-content>
                  </v-list-item>-->
                  <v-list-item
                    v-if="item._isEquipment"
                    @click="printLabels([item])"
                  >
                    <v-list-item-icon>
                      <v-icon class="mr-2">mdi-label-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content
                      >Tulosta kalustotarra
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item v-else @click="editNode(item)">
                    <v-list-item-icon>
                      <v-icon class="mr-2">mdi-arrow-right</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>Avaa sijainti</v-list-item-content>
                  </v-list-item>

                  <v-list-item
                    v-if="!item._isEquipment"
                    @click="deleteNode(item)"
                  >
                    <v-list-item-icon>
                      <v-icon class="mr-2">mdi-delete</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>Poista sijainti</v-list-item-content>
                  </v-list-item>

                  <v-list-item
                    v-if="!item._isEquipment"
                    @click="addEquipment(item)"
                  >
                    <v-list-item-icon>
                      <v-icon class="mr-2">mdi-toy-brick-plus</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>Lisää kalusto</v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-treeview>
        </v-card-text>
      </v-card>

      <kalusto-sijainti-edit-dialog
        ref="kalustoSijaintiEditDialog"
        @close="fetchLocations"
      />

      <!-- Add Equipment Dialog -->
      <v-dialog
        v-model="dialogEquipment"
        max-width="900"
        persistent
        :fullscreen="$vuetify.breakpoint.xsOnly"
      >
        <kalusto-edit
          :dialog="true"
          ref="kalustoEdit"
          @close="
            () => {
              dialogEquipment = false;
              this.fetchEquipments();
            }
          "
        />
      </v-dialog>
      <div id="qr-code" ref="qrCode"></div>
      <picture-dialog v-model="selectedPicture" />
      <qr-code-scanner-dialog @detect="onDetect" v-model="showQrCodeScanner" />
      <v-dialog v-model="dialogPrintLabels" max-width="600" persistent>
        <v-card>
          <v-card-title>Tulosta seuraavat tarrat</v-card-title>
          <v-divider />
          <v-card-text>
            <v-row>
              <v-col cols="12" class="mt-5">
                <v-radio-group v-model="selectedPrintType">
                  <v-radio label="Kaikki" value="all"></v-radio>
                  <v-radio
                    label="Uudemmat kuin (luotu tämän pvm jälkeen)"
                    value="date"
                  ></v-radio>

                  <v-radio label="Listaa IDt" value="id"></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="12">
                <modal-picker
                  v-if="selectedPrintType === 'date'"
                  v-model="selectedPrintDate"
                  label="Uudemmat kuin"
                />
                <v-textarea
                  v-if="selectedPrintType === 'id'"
                  v-model="selectedPrintIds"
                  auto-grow
                  label="IDt"
                  hint="Erottele IDt rivinvaihdolla"
                  outlined
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="dialogPrintLabels = false" text>Sulje</v-btn>
            <v-btn color="primary" @click="printLabelsWithSelectedEquipments"
              >Tulosta</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="dialogMoveEquipment" max-width="600" persistent>
        <v-card>
          <v-card-title>Siirrä kalusto eri sijaintiin</v-card-title>
          <v-divider />
          <v-card-text v-if="selectedNodes.length">
            <v-row>
              <v-col cols="12" class="mt-5">
                <div class="text-subtitle-1">
                  Siirrettävä kalusto:
                  <strong>{{ selectedEquipments.length }}</strong> kpl
                </div>
                <v-row
                  v-for="equipment in selectedEquipments"
                  :key="equipment.id"
                  class="mr-2 mt-2"
                >
                  <v-col cols="10">
                    <div style="text-wrap: auto">{{ equipment.name }}</div>
                    <div class="font-italic caption">
                      {{ equipment.id }}
                    </div>
                    <div class="font-italic caption">
                      {{ equipment._fullTree }}
                    </div>
                  </v-col>
                  <v-col cols="2">
                    <v-row no-gutters>
                      <v-spacer />
                      <v-btn
                        small
                        icon
                        @click="
                          selectedNodes = selectedNodes.filter(
                            (node) => node !== equipment.id
                          )
                        "
                      >
                        <v-icon small>mdi mdi-delete</v-icon>
                      </v-btn>
                    </v-row>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12">
                <div class="text-subtitle-1">
                  Valitse mihin kalusto siirretään:&nbsp;
                  <v-btn
                    icon
                    @click="
                      $refs.kalustoSijainnitDialogMove.openLocationDialog()
                    "
                    ><v-icon>mdi mdi-arrow-right</v-icon></v-btn
                  >
                </div>
                <div
                  v-if="dialogMoveEquipmentSelectedLocation"
                  class="font-weight-bold text-subtitle-2"
                >
                  {{
                    dialogMoveEquipmentSelectedLocation?._fullTree ||
                    dialogMoveEquipmentSelectedLocation?.name
                  }}
                </div>
              </v-col>
            </v-row>
            <v-row>
              <kalusto-sijainnit-dialog
                ref="kalustoSijainnitDialogMove"
                @select="confirmLocationSelectionForMove"
              />
            </v-row>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="dialogMoveEquipment = false" text>Sulje</v-btn>
            <v-btn
              color="primary"
              @click="moveSelectedEquipments"
              :loading="loadingMoveEquipment"
              >Tallenna</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="dialogMoveTo" width="500" persistent>
        <v-card>
          <v-card-title>
            <v-icon class="mr-2">mdi-bus-marker</v-icon>
            Siirry suoraan kalustoon
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-row class="mt-2">
              <v-col cols="12">
                <v-text-field
                  v-model="moveToEquipmentId"
                  outlined
                  label="Syötä kaluston ID"
                  hide-details
                  autofocus
                  @keydown.enter="moveToEquipment(moveToEquipmentId)"
                >
                  <template v-slot:append>
                    <v-btn
                      @click="moveToEquipment(moveToEquipmentId)"
                      color="primary"
                      icon
                      style="margin-top: -5px !important"
                      ><v-icon>mdi mdi-arrow-right</v-icon></v-btn
                    >
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              @click="
                () => {
                  moveToEquipmentId = '';
                  dialogMoveTo = false;
                }
              "
              text
              >Sulje</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-row>
</template>

<style scoped lang="scss">
:deep(.v-treeview-node__prepend:empty) {
  display: none;
}

@media (max-width: 599px) {
  :deep(.v-treeview-node__level) {
    width: 12px;
  }
  :deep(.v-treeview-node__prepend) {
    width: 18px;
  }
}
</style>
